import _ from 'lodash';
import mapboxGl from 'mapbox-gl';
import unitFormatConverter from 'Services/unitFormatConverter/unitFormatConverter';
import { isNil } from 'lodash';

const iconsMapping = {
    'Unknown': 'danger',
    'Accident': 'accident',
    'Fog': 'fog',
    'Dangerous Conditions': 'danger',
    'Rain': 'rain',
    'Ice': 'ice',
    'Jam': 'incident',
    'Lane Closed': 'laneclosed',
    'Road Closed': 'roadclosed',
    'Road Works': 'roadworks',
    'Wind': 'wind',
    'Flooding': 'flooding',
    'Detour': 'detour',
    'Cluster': ''
};

// eslint-disable
/**
 * The IncidentMarker class is used to display incident markers on the map.
 * Clustered incidents are shown as a number and all the other incidents are shown with their appropriate category icon.
 *
 * The structure of the marker is very simple:
 * <div class="icon-marker icon-circle-..." style="opacity: 0.9; ...">
 *      <div class="icon-marker-inner ..." style="height: 20px; width: 20px;">
 *      </div>
 * </div>
 *
 * There are 3 ways of customizing the marker:
 * 1) You can do some basic customization such as size and opacity in the options.
 * 2) Pass CSS classes to the outer and inner containers.
 * 3) Use the customElementCallback to pass a function that renders a complete custom HTML element.
 * @class IncidentMarker
 * @constructor
 * @param {Object} [options] Marker options
 * @param {Object} [renderData] Incident Details feature: This is data provided by the service to render the marker.
 *
 * @param {Boolean} [options.imperialUnits = false] Set units to imperial.
 * @param {Array} [options.offset = [0, 0]] Marker offset - Negative offsets indicate left and up.
 * @param {Number|Array} [options.popupOffset = 15] Popup offset.
 * Single number, vertical distance from the point or Array.
 * Negative offsets indicate left and up.
 * @param {Boolean} [options.showDefaultPopup = true] Shows default popup.
 *
 * @param {Number} [options.size = 20] Width and height of the marker.
 * @param {Number} [options.opacity = 0.9] Opacity of the marker.
 * @param {String} [options.outerClass] Add an extra CSS class to the outer element of the marker.
 * @param {String} [options.innerClass] Add an extra CSS class to the inner element of the marker.
 * @param {Function} [options.customElementCallback] A callback function to render a custom element for the marker.
 * In the callback you have access to all the features provided by the service `customElementCallback(feature)`.
 * This function must return a HTML element.
 *
 * @param {Array} [renderData.geometry.coordinates] The marker center point (lng lat).
 * @param {String} [renderData.properties.incidentCategory] The category of the incident.
 * @param {String} [renderData.properties.incidentSeverity] The severity of the incident.
 * @param {String} [renderData.properties.clusterSize] The number of elements in the cluster.
 */
// eslint-disable
class IncidentMarker {
    constructor(options = {}, renderData) {

        this.options = options;
        this.renderData = this._validateRenderData(renderData);
        this.isCluster = this.renderData.properties && !isNil(this.renderData.properties.clusterSize);

        this._marker = new mapboxGl.Marker(this._render());
        this._marker.setLngLat(renderData.geometry.coordinates);
        this._setPopup();

        this.update = this.update.bind(this);
        this.getElement = this.getElement.bind(this);
        this.getPopup = this.getPopup.bind(this);
        this.remove = this.remove.bind(this);
        this.addTo = this.addTo.bind(this);
    }

    _validateRenderData = (renderData) => {
        if (!(_.has(renderData, 'geometry.coordinates') &&
            _.has(renderData, 'properties') &&
            !_.isEmpty(renderData.properties))) {
            throw new Error('Incident Marker: RenderData has properties missing to render the element. ' +
                'Required properties: geometry.coordinates; properties;');
        }
        return renderData;
    };

    _getOptions = (severity, incidentCategory) => {
        const acceptedOptions = ['opacity', 'size'];
        const incidentCategoryIcon = this.isCluster ? '' : iconsMapping[incidentCategory];
        const newOptions = Object.assign({}, {
            outerClass: `tt-icon-marker tt-icon-circle-${severity} ${this.options.outerClass || ''}`,
            innerClass: `tt-icon-marker-inner tt-icon-${incidentCategoryIcon} ${this.options.innerClass || ''}`
        });

        Object.keys(this.options).forEach((opt) => {
            if (acceptedOptions.includes(opt)) {
                newOptions[opt] = this.options[opt];
            }
        });

        return newOptions;
    };

    _renderCustomElement = (customElementCallback) => {
        if (typeof customElementCallback !== 'function') {
            throw new Error('customElementCallback must be a function');
        }

        const customElem = customElementCallback(this.renderData);

        if (!(customElem instanceof window.HTMLElement)) {
            throw new Error('customElementCallback must return a HTML element');
        }

        return customElem;
    }

    _renderMarker = (options) => {
        const outerEl = document.createElement('div');
        outerEl.className = options.outerClass;
        if (options.opacity) {
            outerEl.style.opacity = options.opacity;
        }

        const innerEl = document.createElement('div');
        innerEl.className = options.innerClass;
        if (options.size) {
            innerEl.style.height = `${options.size}px`;
            innerEl.style.width = `${options.size}px`;
            innerEl.style.lineHeight = `${options.size}px`;
            innerEl.style.fontSize = `${options.size / 2}px`;
        }

        if (this.isCluster) {
            const clusterSize = this.renderData.properties.clusterSize;
            innerEl.innerText = clusterSize < 100 ? clusterSize : '99+';
        }

        outerEl.appendChild(innerEl);
        return outerEl;
    }

    _onClusterClick = () => {
        const { properties } = this.renderData;
        if (properties.clusterBounds) {
            this.map.fitBounds([
                properties.clusterBounds[0].reverse(), // lng lan
                properties.clusterBounds[1].reverse() // lng lan
            ]);
            return;
        }

        this.map.flyTo({
            center: this.renderData.geometry.coordinates,
            zoom: this.map.getZoom() + 1
        });
    }

    _bindEventListeners = () => {
        if (this.isCluster) {
            this._marker.getElement().addEventListener('click', this._onClusterClick);
        }
    }

    _removeEventListeners = () => {
        if (this.isCluster) {
            this._marker.getElement().removeEventListener('click', this._onClusterClick);
        }
    }

    _setPopup = () => {
        if (this.isCluster || this.options.showDefaultPopup === false) {
            return;
        }

        this._popupInstance = this._createPopup();
        this._marker.setPopup(this._popupInstance);
    }

    _validateSeverity = (properties) => properties && properties.incidentSeverity || 'unknown';

    _validateIncidentCategory = (properties) => properties && properties.incidentCategory || 'Unknown';

    _validateTime = (properties) => properties && unitFormatConverter.formatTime(properties.delaySeconds);

    _validateDistance = (properties) => properties.lengthMeters &&
        unitFormatConverter.formatDistance(properties.lengthMeters, this.options.imperialUnits);

    _validateRoadNumber = (properties) => properties && properties.roadNumber;

    _createPopupContent = () => {
        const properties = this.renderData.properties;

        const severity = this._validateSeverity(properties);
        const incidentCategory = this._validateIncidentCategory(properties);
        const time = this._validateTime(properties);
        const distance = this._validateDistance(properties);
        const roadNumber = this._validateRoadNumber(properties);

        return (
            `<div class="tt-traffic-details">
                <div class="tt-traffic-icon">
                    <div class="tt-icon-circle-${severity}">
                        <div class="tt-icon-${iconsMapping[incidentCategory]}"></div>
                    </div>
                </div>
                ${roadNumber ? `<div class="tt-road-shield">${roadNumber}</div>` : ''}
                <div class="tt-traffic-description">
                    <div class="tt-incident-category">${incidentCategory}</div>
                    <div
                        class="tt-incident-delay-length"
                        ${!time && !distance ? 'style="display:none;"' : ''}
                    >
                            ${time ? `<div class="tt-incident-delay">${time}</div>` : ''}
                            ${distance ? `<div class="tt-incident-length">${distance}</div>` : ''}
                    </div>
                </div>
            </div>`);
    }

    _createPopup = () => {
        return new mapboxGl.Popup({ offset: this.options.popupOffset || 15 })
            .setHTML(this._createPopupContent());
    }

    _render = () => {
        if (this.options.customElementCallback) {
            return this._renderCustomElement(this.options.customElementCallback);
        }

        const { properties } = this.renderData;
        const severity = this._validateSeverity(properties);
        const incidentCategory = this._validateIncidentCategory(properties);

        return this._renderMarker(this._getOptions(severity, incidentCategory));
    }

    update(renderData) {
        this._validateRenderData(renderData);

        if (_.isEqual(renderData, this.renderData)) {
            return;
        }

        this._marker.setLngLat(renderData.geometry.coordinates);

        this.renderData = renderData;

        if (!this._popupInstance) {
            this._popupInstance = this._createPopup();
            this._marker.setPopup(this._popupInstance);
        } else {
            this._popupInstance.setHTML(this._createPopupContent());
        }
    }

    getElement() {
        return this._marker.getElement();
    }

    getPopup() {
        return this._marker.getPopup();
    }

    remove() {
        this._removeEventListeners();
        this._marker.remove();
    }

    addTo(map) {
        this.map = map;
        this._marker.addTo(map.__om);
        this._bindEventListeners();
        return this;
    }
}

export default IncidentMarker;



// WEBPACK FOOTER //
// ./map/display/markers/IncidentMarker.js